import uuid4 from "uuid4";
import ChatCache from "./ChatCache";
import constants from "./Constants";
import { getVariant } from "./Experiment";

// Client-side behavioral analytics for the embedded widget.
// Events are queued and shipped in batches to the SaaS-Backend; whatever is
// still queued when the guest leaves the page goes out via sendBeacon.
// The whole tracker is fire-and-forget: it must never break the chat.

type QueuedEvent = {
  eventType: string;
  occurredAt: string;
  threadId: string | null;
  pageUrl: string;
  payload: any;
};

const FLUSH_DELAY_MS = 4000;
const MAX_QUEUE = 20;

class Analytics {
  private queue: QueuedEvent[] = [];
  private assistantId: string | null = null;
  private language: string = "en";
  private enabled: boolean = false;
  private sessionId: string = uuid4();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEventKey: string = "";
  private lastEventAt: number = 0;
  private chatCache = new ChatCache();

  public init(options: {
    assistantId?: string;
    language?: string;
    isEditor?: boolean;
  }) {
    if (!constants.FEATURE_FLAGS.ANALYTICS) return;
    // The editor preview inside Drastik must not pollute the metrics.
    if (!options.assistantId || options.isEditor) return;

    this.assistantId = options.assistantId;
    this.language = options.language ?? "en";

    if (this.enabled) return;
    this.enabled = true;

    // pagehide covers tab close / navigation on both desktop and iOS Safari;
    // visibilitychange catches tab switches (e.g. right after a booking-link
    // click opens a new tab) and mobile app-switches that never fire pagehide.
    window.addEventListener("pagehide", () => this.flush());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") this.flush();
    });
  }

  public track(eventType: string, payload: any = null) {
    if (!this.enabled || !this.assistantId) return;

    try {
      // Re-renders and store round trips can double-fire the same event;
      // an identical event within a second is noise, not behavior.
      const key = eventType + JSON.stringify(payload ?? null);
      const now = Date.now();
      if (key === this.lastEventKey && now - this.lastEventAt < 1000) return;
      this.lastEventKey = key;
      this.lastEventAt = now;

      this.queue.push({
        eventType,
        occurredAt: new Date().toISOString(),
        threadId: this.chatCache.getThreadId(this.assistantId) ?? null,
        pageUrl: window.location.href,
        // The experiment arm rides inside every event payload: the ingest DTO
        // ignores unknown top-level fields, but Payload is persisted raw.
        payload: { ...(payload ?? {}), variant: getVariant() },
      });

      if (this.queue.length >= MAX_QUEUE) {
        this.flush();
        return;
      }

      if (this.flushTimer) clearTimeout(this.flushTimer);
      this.flushTimer = setTimeout(() => this.flush(), FLUSH_DELAY_MS);
    } catch {
      // never let tracking break the widget
    }
  }

  private flush() {
    if (!this.queue.length || !this.assistantId) return;

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const events = this.queue;
    this.queue = [];

    try {
      const body = JSON.stringify({
        assistantId: this.assistantId,
        fingerprint: this.chatCache.getFingerprint(),
        sessionId: this.sessionId,
        device: window.innerWidth <= 768 ? "mobile" : "desktop",
        language: this.language,
        referrer: document.referrer || null,
        events,
      });

      const url = this.getBackendBaseUrl() + "WidgetEvents/ingest";

      // NOT sendBeacon: beacons can't send application/json cross-origin
      // (no CORS preflight allowed), so Chrome silently drops them — which
      // ate exactly the link_click queued right before the booking tab took
      // focus. keepalive fetch survives unload AND does real CORS.
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // swallow — analytics must stay invisible to the guest
    }
  }

  private getBackendBaseUrl() {
    return process.env.REACT_APP_IS_PROD !== "yes"
      ? process.env.REACT_APP_API_DEV
      : process.env.REACT_APP_API_PROD;
  }
}

const analytics = new Analytics();
export default analytics;
